import { Link } from '@tanstack/react-router';
import { Bot, Plus } from 'lucide-react';
import { AgentAvatar } from '@/components/agent-avatar';
import { AgentDialog } from '@/components/agent-dialog';
import { DeleteAgentIcon } from '@/components/delete-agent-button';
import { Page, PageHeader } from '@/components/page';
import { AgentStatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAgents, useWorkspaceId } from '@/lib/hooks';
import { RUNTIME_TYPE } from '@/lib/labels';
import { t } from '@/lib/i18n';
import { runtimeReady } from '@/routes/start';

export function AgentsPage() {
  const workspaceId = useWorkspaceId();
  const agents = useAgents(workspaceId);

  return (
    <Page>
      <PageHeader
        title={t('Agents')}
        description={t('Agents are AI team members with fixed instructions. They work through work items — always one agent per work item.')}
        actions={
          <AgentDialog workspaceId={workspaceId}>
            <Button>
              <Plus /> {t('Create agent')}
            </Button>
          </AgentDialog>
        }
      />
      {agents.isPending ? (
        <LoadingRows rows={4} />
      ) : agents.isError ? (
        <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />
      ) : agents.data.length === 0 ? (
        <EmptyState
          icon={<Bot />}
          title={t('No agents yet')}
          description={t('Create an agent and give it instructions. Then you can assign it to a desk.')}
          action={
            <AgentDialog workspaceId={workspaceId}>
              <Button>
                <Plus /> {t('Create your first agent')}
              </Button>
            </AgentDialog>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {agents.data.map((a) => (
            // The delete button sits beside the link: dialog clicks would otherwise bubble (through the portal) into it.
            <Card key={a.id} className="group relative flex flex-col transition-colors hover:border-primary/40">
              <Link
                to="/w/$workspaceId/agents/$agentId"
                params={{ workspaceId, agentId: a.id }}
                className="flex flex-1 flex-col gap-3 p-4"
              >
                <div className="flex items-start gap-3 pr-8">
                  <AgentAvatar seed={a.id} size={52} muted={a.status === 'disabled'} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold group-hover:text-primary">{a.name}</span>
                      {a.status === 'disabled' && <AgentStatusBadge status={a.status} />}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.description || t('No description')}</p>
                  </div>
                </div>
                {a.capabilities.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {a.capabilities.slice(0, 4).map((c) => (
                      <Badge key={c} tone="outline" className="text-[11px]">
                        {c}
                      </Badge>
                    ))}
                    {a.capabilities.length > 4 && <Badge tone="muted">+{a.capabilities.length - 4}</Badge>}
                  </div>
                )}
                <div className="mt-auto border-t pt-3 text-xs text-muted-foreground">
                  {a.runtimeProfile ? (
                    <>
                      <div className="truncate text-foreground">
                        {a.runtimeAutomatic && <span className="text-muted-foreground">{t('Automatic → ')}</span>}
                        {a.runtimeProfile.name}
                        {!runtimeReady(a.runtimeProfile) && <span className="text-amber-700 dark:text-amber-400"> · {t('not connected')}</span>}
                      </div>
                      <div className="truncate">
                        {RUNTIME_TYPE[a.runtimeProfile.runtimeType] ?? a.runtimeProfile.runtimeType}
                        {a.runtimeProfile.runtimeType !== 'local' && a.effectiveModel && (
                          <span className="font-mono"> · {a.effectiveModel}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400">{a.runtimeAutomatic ? t('Automatic · none available') : t('No runtime')}</span>
                  )}
                </div>
              </Link>
              <div className="absolute top-2 right-2">
                <DeleteAgentIcon agent={a} workspaceId={workspaceId} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
